import { FC, HTMLAttributes, ReactNode } from 'react';

import { Pagination, PaginationProps } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useComponentId } from '@telialt/utils/src/hooks';

import { List } from './CardsList.styles';
import { PAGINATION_RESULTS_PER_PAGE_DEFAULT_SIZE, PAGINATION_RESULTS_PER_PAGE_SIZES } from './constants';

type Inheritables = Omit<HTMLAttributes<HTMLUListElement>, 'children'>;
type PaginationInherited = Pick<
    PaginationProps,
    'onPageChange' | 'currentPage' | 'totalItems' | 'pageSize' | 'hrefGetter' | 'navigationFunction' | 'linkElement'
>;
type OwnProps = { listItems: ReactNode[]; 'data-test'?: string };
type Props = OwnProps & PaginationInherited & Inheritables;

export const CardsList: FC<Props> = ({
    listItems,
    totalItems,
    currentPage,
    pageSize = PAGINATION_RESULTS_PER_PAGE_DEFAULT_SIZE,
    onPageChange,
    hrefGetter,
    navigationFunction,
    linkElement,
    'data-test': dataTest,
    ...inherited
}) => {
    const UID = useComponentId('cards-list');
    const { t } = useTranslations();

    return (
        <>
            <List data-test={dataTest} {...inherited}>
                {listItems.map((item, index) => (
                    <li key={index}>{item}</li>
                ))}
            </List>
            <Pagination
                currentPage={currentPage}
                onPageChange={onPageChange}
                totalItems={totalItems}
                availablePageSizes={PAGINATION_RESULTS_PER_PAGE_SIZES}
                pageSize={pageSize}
                asLink={true}
                hrefGetter={hrefGetter}
                navigationFunction={navigationFunction}
                linkElement={linkElement}
                pageSizeLabel={t['blog.component.pagination.items_per_page_label']}
                outOfLabel={t['blog.component.pagination.out_of_label']}
                pageSelectorId={`${UID}-mobile-page-selector`}
                pageSelectorLabel={t['blog.component.pagination.mobile_page_selector_label']}
                nextButtonAriaLabel={t['blog.component.pagination.next_page']}
                nextButtonText={t['blog.component.pagination.next_page']}
                previousButtonAriaLabel={t['blog.component.pagination.previous_page']}
                previousButtonText={t['blog.component.pagination.previous_page']}
                stepNumberPrefix={t['blog.component.pagination.step_number_prefix']}
                data-test={dataTest && `${dataTest}--pagination`}
            />
        </>
    );
};
